import { useState, useEffect, useMemo } from 'react';
import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import cn from 'classnames';
import dynamic from 'next/dynamic';
// Api
import { ADD_MERCH_ITEM_TO_CART } from 'api/merch/mutations';
import { GET_MERCH_CART_ITEM_LIST } from 'api/merch/queries';
import { ME } from 'api/auth/queries';
// Types
import { Age, Gender, MerchType } from 'api/graphql-global-types';
import {
  AddMerchItemToCart,
  AddMerchItemToCartVariables,
} from 'api/merch/types/AddMerchItemToCart';
import { GetStoreMerchProduct_getStoreMerchProduct } from 'api/merch/types/GetStoreMerchProduct';
// Lib
import * as ga from 'lib/google-analytics';
import * as ttq from 'lib/ttpixel';
import * as fbq from 'lib/fpixel';
// Helpers
import {
  getAllMerchOptions,
  getColorName,
  getLowestPriceVariant,
  getMerchGenders,
  getSelectedVariant,
  getSimilarVariant,
  getTotalMerchOptions,
  sortMerchProductVariantsByMainCustomImages,
} from 'helpers/merch';
import { getEnvLink } from 'helpers/routes';
import { formatCurrencyString } from 'helpers/formatCurrencyString';
// Hooks
import { useAppContext, useGetCurrUser } from 'hooks';
// UI
import Button from 'ui3/Button/Button';
import HashTags from 'ui3/HashTags/HashTags';
import Text from 'ui3/Text/Text';
// Components
import ProductImages, {
  ProductImageType,
} from 'components/common3/ProductImages/ProductImages';
import DetailsAndPolicies from 'components/common3/DetailsAndPolicies/DetailsAndPolicies';
import SecurePayments from 'components/common3/EcommerceTrustBadges/SecurePayments/SecurePayments';
import { showToast } from 'components/common/Toast/Toast';
import QuantityAndPrice from 'components/common3/QuantityAndPrice/QuantityAndPrice';
import MerchSizeOptions from './components/MerchSizeOptions/MerchSizeOptions';
import MerchOptions, {
  MerchOption,
} from './components/MerchOptions/MerchOptions';
import MerchColorPicker, {
  MerchColorOption,
} from './components/MerchColorPicker/MerchColorPicker';
import TalentBlock from 'components/common3/TalentBlock/TalentBlock';
import SocialShareQRButton from 'components/common3/SocialShareQRButton/SocialShareQRButton';
// Styles
import styles from './Merch.module.scss';
// Dynamic imports
const RelatedProductsList = dynamic(
  () => import('../Athlete/components/RelatedProductsList/RelatedProductsList'),
  {
    ssr: false,
  }
);
const SizeGuideModal = dynamic(
  () => import('./components/SizeGuideModal/SizeGuideModal'),
  {
    ssr: false,
  }
);

type MerchProps = {
  merch: GetStoreMerchProduct_getStoreMerchProduct;
};

const Merch = ({ merch }: MerchProps) => {
  const router = useRouter();
  const {
    gender: linkGender,
    color: linkColor,
    size: linkSize,
    athleteSlug,
    productSlug,
  } = router.query;

  const { guestToken, addMerchItemToShoppingCart } = useAppContext();
  const { data: userData, updateQuery } = useGetCurrUser();
  const isLogged = !!userData?.me;

  const [
    addMerchItemToCart,
    { loading: addMerchItemToCartLoading },
  ] = useMutation<AddMerchItemToCart, AddMerchItemToCartVariables>(
    ADD_MERCH_ITEM_TO_CART
  );

  const merchName = merch?.title || '';
  const variants = sortMerchProductVariantsByMainCustomImages(
    merch?.merchProductVariants
  );

  const productType = merch?.type || undefined;

  const isHat = productType === MerchType.Hat;

  const isYouth = !!variants.find((variant) => variant.age === Age.Youth);

  const genders = new Set<string>(getMerchGenders(variants));
  const outsideLinkVariant = getSelectedVariant({
    size: `${linkSize}`,
    gender: `${linkGender}` === Age.Youth ? Gender.Unisex : `${linkGender}`,
    color: `${linkColor}`,
    variants,
    productType,
  });
  const lowestPriceVariant = getLowestPriceVariant(variants, productType);

  const initialColor = outsideLinkVariant
    ? `${linkColor}`
    : `${lowestPriceVariant?.color}-${lowestPriceVariant?.colorGroupId}`;
  const initialSize = outsideLinkVariant
    ? `${linkSize}`
    : lowestPriceVariant?.size || 'L';
  const initialGender = outsideLinkVariant
    ? outsideLinkVariant.gender || ''
    : lowestPriceVariant?.gender || '';

  const [size, setSize] = useState<string>(initialSize);
  const [color, setColor] = useState<string>(initialColor);
  const [amount, setAmount] = useState<number>(1);
  const [gender, setGender] = useState<string>(initialGender);

  const [
    sizeGuideModalIsVisible,
    setSizeGuideModalIsVisible,
  ] = useState<boolean>(false);
  const [
    addToCartLoadingSimulation,
    setAddToCartLoadingSimulation,
  ] = useState<boolean>(false);

  useEffect(() => {
    if (initialSize) {
      setSize(initialSize);
    }

    if (initialColor) {
      setColor(initialColor);
    }

    if (initialGender) {
      setGender(initialGender);
    }
  }, [initialColor, initialSize, initialGender]);

  const addParametersToUrl = ({
    newGender = gender,
    newSize = size,
    newColor = color,
  }) => {
    const baseUrl = `/${athleteSlug}/merch/${productSlug}`;

    const shallowPushURL = `${baseUrl}${'?gender=' + newGender}${
      '&color=' + newColor
    }${'&size=' + newSize}`;

    router.push(shallowPushURL, undefined, {
      shallow: true,
    });
  };

  const selectedVariant = getSelectedVariant({
    size,
    gender,
    color,
    variants,
    productType,
  });

  const allVariantsForGender = getAllMerchOptions({
    size,
    gender,
    color,
    variants,
  });

  const totalVariantsForGender = getTotalMerchOptions({
    size,
    gender,
    color,
    variants,
  });

  const variantId = selectedVariant?.id;

  const price = selectedVariant?.price || 0;
  const productImages = useMemo<ProductImageType[]>(() => {
    const merchImages = selectedVariant?.mockupImages || [];
    const customImages = selectedVariant?.customImages || [];
    const list: ProductImageType[] = [];
    if (customImages.length) {
      customImages.forEach((image) => {
        list.push({
          id: image.id,
          isMainImage: !!image.isMainImage,
          imageUrl: image.customMerchImageURL,
        });
      });
    }
    merchImages.forEach((image) => {
      list.push({
        id: `${image.id}`,
        isMainImage: false,
        imageUrl: image.mockupImageURL,
      });
    });
    const mainImage = list.find((image) => image.isMainImage);
    if (!mainImage && list.length > 0) {
      list[0].isMainImage = true;
    }
    return list;
  }, [selectedVariant?.mockupImages, selectedVariant?.customImages]);

  const handleAddToCartClick = async () => {
    const productVariant = variants?.find(
      (item) => item.id === Number(variantId)
    );
    const productPrice = Number((Number(amount) * Number(price)).toFixed(2));
    const currency = 'USD';

    if (isLogged || guestToken) {
      try {
        await addMerchItemToCart({
          variables: {
            input: {
              items: [{ variantId: Number(variantId), amount: amount }],
            },
          },
          onCompleted: (data) => {
            updateQuery((prevUserData) => ({
              ...prevUserData,
              me: {
                ...prevUserData.me,
                cartSize: data.addMerchItemToCart.length,
              },
            }));
          },
          ...(isLogged && {
            refetchQueries: [{ query: ME }],
          }),
          ...(guestToken && {
            refetchQueries: [
              {
                query: GET_MERCH_CART_ITEM_LIST,
              },
            ],
          }),
        });

        if (guestToken && productVariant && merch) {
          addMerchItemToShoppingCart({
            variantId: Number(variantId),
            amount: Number(amount),
            merchProductVariant: {
              ...productVariant,
              merchProduct: merch,
            },
          });
        }
      } catch (error) {
        console.log('error', error);
      }

      showToast({
        message: 'Product successfully added to cart',
      });
    } else {
      if (productVariant && merch) {
        addMerchItemToShoppingCart({
          variantId: Number(variantId),
          amount: Number(amount),
          merchProductVariant: {
            ...productVariant,
            merchProduct: merch,
          },
        });

        fbq.event('AddToCart', {
          value: productPrice,
          currency: 'USD',
          contents: [
            {
              id: merch?.id,
              quantity: amount,
              price: productPrice,
            },
          ],
          content_type: 'product',
          content_ids: [merch?.id],
        });

        setAddToCartLoadingSimulation(true);

        setTimeout(() => {
          setAddToCartLoadingSimulation(false);

          showToast({
            message: 'Product successfully added to cart',
          });
        }, 500);
      }
    }

    ga.addToCart({
      currency,
      value: productPrice,
      items: [
        {
          id: `${merch?.id}`,
          name: merchName,
          category: productType || '',
          brand: merch?.store?.storeDetails?.storeName || '',
          quantity: amount,
          price: Number(price),
          variant: color,
        },
      ],
    });

    ttq.addToCart({
      content_id: `${merch?.id}`,
      content_type: 'product',
      content_name: merchName,
      content_category: productType || '',
      quantity: amount,
      price: Number(price),
      value: productPrice,
      currency,
    });
  };

  const onSetGender = (newGender: string) => {
    const similarVariant = getSimilarVariant({
      size,
      gender: newGender,
      color,
      variants,
    });

    addParametersToUrl({
      newSize: similarVariant.size,
      newColor: similarVariant.color,
      newGender: newGender === Gender.Unisex && isYouth ? Age.Youth : newGender,
    });
  };

  const onSetSize = (newSize: string) => {
    addParametersToUrl({
      newSize,
    });
    setSize(newSize);
  };

  const onSetColor = (newColor: string) => {
    addParametersToUrl({
      newColor,
    });
    setColor(newColor);
  };

  const onSizeGuideModalOpen = () => setSizeGuideModalIsVisible(true);

  const onSizeGuideModalClose = () => setSizeGuideModalIsVisible(false);

  const handleAmountChange = (amount: number) => {
    setAmount(amount);
  };

  const amountPrice = amount ? Number(price) * Number(amount) : Number(price);
  const totalPrice = formatCurrencyString(amountPrice);
  const addToCartLoading =
    addMerchItemToCartLoading || addToCartLoadingSimulation;

  const genderOptions: MerchOption[] = [];
  genders.forEach((element) => {
    genderOptions.push({ value: element, isSelected: element === gender });
  });

  const availableColorOptions: MerchColorOption[] = [];
  const totalColorOptions: MerchColorOption[] = [];

  const isJoggers = merch.type === MerchType.Joggers;

  allVariantsForGender.colors.forEach((option) => {
    const variant = variants.find(
      (element) => getColorName(element) === option && element.gender === gender
    );

    const imageUrls: string[] = [];

    variant?.mockupImages?.forEach(
      (image) => image.mockupImageURL && imageUrls.push(image.mockupImageURL)
    );

    if (isJoggers && option.includes('Team Royal')) {
      return; // color team royal was removed by the printful for joggers only
    }

    availableColorOptions.push({
      value: option,
      isSelected: option === color,
      imageUrl: imageUrls[0],
      colorCode: variant?.colorCode ?? '',
    });
  });

  totalVariantsForGender.colors.forEach((option) => {
    const variant = variants.find(
      (element) => getColorName(element) === option && element.gender === gender
    );

    const imageUrls: string[] = [];

    variant?.mockupImages?.forEach(
      (image) => image.mockupImageURL && imageUrls.push(image.mockupImageURL)
    );

    if (isJoggers && option.includes('Team Royal')) {
      return; // color team royal was removed by the printful for joggers only
    }

    totalColorOptions.push({
      value: option,
      isSelected: option === color,
      imageUrl: imageUrls[0],
      colorCode: variant?.colorCode ?? '',
    });
  });

  // This part is covering the case when Printful is returning null for HAT size value
  if (isHat && !merch?.merchProductVariants[0].size) {
    variants.forEach((option) => {
      const imageUrls: string[] = [];

      option?.mockupImages?.forEach(
        (image) => image.mockupImageURL && imageUrls.push(image.mockupImageURL)
      );

      availableColorOptions.push({
        value: option.color,
        isSelected: getColorName(option) === color,
        imageUrl: imageUrls[0],
        colorCode: option.colorCode ?? '',
      });
    });
  }

  const availableSizeOptions: MerchOption[] = [];
  const totalSizeOptions: MerchOption[] = [];
  allVariantsForGender.sizes.forEach((option) => {
    availableSizeOptions.push({
      value: option,
      isSelected: option === size,
    });
  });

  totalVariantsForGender.sizes.forEach((option) => {
    totalSizeOptions.push({
      value: option,
      isSelected: option === size,
    });
  });

  const isUserOwner = userData?.me?.id === merch?.store?.id;
  const profilePath = `/${athleteSlug}`;
  const storeName = merch?.store?.storeDetails?.storeName || '';
  return (
    <div className={styles.root}>
      <div className={styles.merchContainer}>
        <section className={styles.imageContainer}>
          <div className={styles.mobileView}>
            <TalentBlock
              store={merch?.store}
              talentName={storeName}
              avatarURL={merch?.store?.storeDetails?.avatarURL || ''}
              profilePath={profilePath}
              showFollowButton={!isUserOwner}
              className={styles.talentBlock}
            />
          </div>

          <ProductImages images={productImages} productName={merchName} />
        </section>

        <section className={styles.merchSection}>
          <div className={styles.desktopView}>
            <TalentBlock
              store={merch?.store}
              talentName={storeName}
              avatarURL={merch?.store?.storeDetails?.avatarURL || ''}
              profilePath={profilePath}
              showFollowButton={!isUserOwner}
              className={styles.talentBlock}
            />
          </div>

          <Text as="h1" variant="h5">
            {merchName}
          </Text>

          <HashTags
            variant="secondary"
            tags={merch?.hashtags || []}
            className={styles.hashTags}
          />

          {!isHat && (
            <>
              <div className={styles.merchOptions}>
                <div className={styles.merchOptionHeader}>
                  <Text variant="subtitle2">Gender:</Text>
                </div>
                <MerchOptions
                  isYouth={isYouth}
                  options={genderOptions}
                  handleSelectOption={onSetGender}
                />
              </div>
              <div className={styles.merchOptions}>
                <div className={styles.merchOptionHeader}>
                  <Text variant="subtitle2">Size:</Text>
                  <Button
                    subVariant="text"
                    className={cn(styles.subtitle, styles.sizeGuide)}
                    onClick={onSizeGuideModalOpen}
                  >
                    View Size Guide
                  </Button>
                </div>
                <MerchSizeOptions
                  availableSizeOptions={availableSizeOptions}
                  totalSizeOptions={totalSizeOptions}
                  handleSelectOption={onSetSize}
                />
              </div>
            </>
          )}

          <div className={styles.merchOptions}>
            <div className={styles.merchOptionHeader}>
              <Text variant="subtitle2">Color:</Text>
            </div>
            <MerchColorPicker
              availableOptions={availableColorOptions}
              allOptions={totalColorOptions}
              onChange={onSetColor}
            />
          </div>

          <QuantityAndPrice
            maxQuantity={99}
            value={amount}
            onChange={handleAmountChange}
            totalPrice={totalPrice}
            showQuantitySelector
            classNames={{
              wrapper: styles.quantityAndPriceWrapper,
              quantityAndPrice: styles.quantityAndPrice,
            }}
          />

          <div className={styles.actionButtonsWrapper}>
            <Button
              onClick={handleAddToCartClick}
              loading={addToCartLoading}
              className={styles.addToCartButton}
              icon="shoppingCart"
            >
              Add To Cart
            </Button>
            <SocialShareQRButton
              modalTitle="Share Merch"
              modalText="Share this merch with your friends"
              path={`${getEnvLink()}/${athleteSlug}/merch/${productSlug}`}
              twitterText={`Check out my latest merch drop on @millionsdotco. Order now while inventory lasts! #newmerch #MILLIONSco`}
              icon
              onlyIcon
            />
          </div>

          <SecurePayments />

          <div className={styles.merchProductDetailsSection}>
            <DetailsAndPolicies
              productType={productType}
              isYouth={isYouth && gender === Gender.Unisex}
            />

            <DetailsAndPolicies showShipping />
          </div>
        </section>
      </div>

      {!isHat && (
        <SizeGuideModal
          isOpen={sizeGuideModalIsVisible}
          onClose={onSizeGuideModalClose}
          productType={productType}
          gender={gender}
        />
      )}

      <RelatedProductsList
        storeId={merch?.store?.id || ''}
        currentId={merch?.id}
        title="You May Also Like"
        seeAllLink={merch?.store?.slug ? `/${merch.store.slug}/merch` : ''}
        seeAllLinkTitle="View Shop"
        showOnlyMerch
      />
    </div>
  );
};

export default Merch;
